// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;

const margins = {
  top: 20,
  right: 70,
  bottom: 25,
  left: 35,
};

let minValue;
let maxValue;

let minTotal;
let maxTotal;

let minDomain;
let maxDomain;


// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');
let annoDate;
let lastData;

// Set your start date filter here (format: 'YYYY-MM-DD')
const startDateFilter = pTime('2025-12-01'); // Change this date as needed
// const startDateFilter = pTime('2025-10-01'); // Change this date as needed


const purple = '#F18851';
const lightPurple = '#b89fd4';
const gray = '#d3d3d3';
const darkGray = '#737373';

// Chart colors
const rollingAvgColor = purple;
const barColor = '#F29462';
const barColorCur = '#C45A28';

// Set CSS custom properties for use in stylesheets
document.documentElement.style.setProperty('--avg-color', barColorCur);
document.documentElement.style.setProperty('--actual-color', barColorCur);

stylePremium(CNNPolitics, CNNBusiness);

d3.json('https://ix.cnn.io/data/shipping-chokepoints/strait-of-hormuz.json').then((json) => {
  console.log(json);
  const { data } = json;
  chartData = data.map(d => ({
    date: d3.timeParse('%Y-%m-%d')(d.date),
    avg: +d.avg,
    total: +d.n_total,
  })).filter(d => d.date >= startDateFilter);

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  minValue = 0;
  maxValue = d3.max(chartData, d => d.avg);

  minTotal = 0;
  maxTotal = d3.max(chartData, d => d.total);

  lastData = chartData.filter(d => d.date == maxDomain)[0];

  // annoDate = chartData.filter(d => d.date > pTime('2023-11-18') && d.date < pTime('2023-11-20'))[0];


  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = isMobile ? 260 : 320;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 3 : 6;
  const yTicks = isMobile ? 5 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const yScaleTotal = d3.scaleLinear()
    .domain([minTotal, maxTotal])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(xTicks)
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) == '01') {
        return d3.timeFormat("%b '%y")(d);
      }
      return d3.timeFormat('%b')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat(d => (d === 0 ? '' : d));

  const path = d3.line()
    .x(d => xScale(d.date))
    .y(d => yScale(d.avg));

  const path2 = d3.line()
    .x(d => xScale(d.date))
    .y(d => yScaleTotal(d.total));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis)
    .selectAll('text')
    .attr('dx', '-4px');

  // Calculate bar width based on the number of data points
  const barWidth = Math.max(1, chartWidth / chartData.length * 0.8);

  // Add vertical bars for total values (in background)
  const bars = g.append('g')
    .attr('class', 'bars');

  bars.selectAll('rect')
    .data(chartData)
    .enter()
    .append('rect')
    .attr('x', d => xScale(d.date) - barWidth / 2)
    .attr('y', d => yScaleTotal(d.total))
    .attr('width', barWidth)
    .attr('height', d => chartHeight - yScaleTotal(d.total))
    .attr('fill', barColor)
    .attr('fill', (d, i) => (i === chartData.length - 1 ? barColorCur : barColor))
    .attr('opacity', (d, i) => (i === chartData.length - 1 ? 1 : 0.8));

  const line = g.append('g')
    .attr('class', 'line');

  // line.append('path')
  //   .datum(chartData)
  //   .attr('stroke', rollingAvgColor)
  //   .attr('stroke-width', '1.5')
  //   .attr('stroke-dasharray', '5,5')
  //   .attr('fill', 'none')
  //   .attr('d', path);

  // line.append('path')
  //   .datum(chartData)
  //   .attr('stroke', barColor)
  //   .attr('stroke-width', '1.5')
  //   .attr('fill', 'none')
  //   .attr('d', path2);

  d3.select('.grid.y .domain').remove();


  // const anno = g.append('g')
  //   .attr('class', 'annotation')
  //   .attr('transform', `translate(${[xScale(annoDate.date), yScale(annoDate.avg)]})`);

  // anno.append('line')
  //   .attr('x1', xScale(d3.timeFormat('%b %-d')(annoDate.date)))
  //   .attr('y1', yScale(80))
  //   .attr('x2', xScale(d3.timeFormat('%b %-d')(annoDate.date)))
  //   .attr('y2', yScale(66))
  //   .style('stroke-width', 1)
  //   .style('stroke', '#999')
  //   .style('fill', 'none');


  // anno.append('text')
  //   .attr('dx', '10px')
  //   .attr('text-anchor', 'end')
  //   .attr('class', 'outline-text')
  //   .text('Houthi attacks in')
  //   .attr('dy', yScale(60.5))
  //   .attr('dx', xScale(d3.timeFormat('%b %-d')(annoDate.date)));

  // anno.append('text')
  //   .attr('dx', '10px')
  //   .attr('text-anchor', 'end')
  //   .attr('class', 'outline-text')
  //   .text('the Red Sea begin')
  //   .attr('dy', yScale(56.5))
  //   .attr('dx', xScale(d3.timeFormat('%b %-d')(annoDate.date)));

  // const annotation = g.append('g')
  //   .attr('class', 'annotation')
  //   .attr('transform', `translate(${[xScale(lastData.date), yScale(lastData.avg)]})`);

  // annotation.append('circle')
  //   .attr('r', '3')
  //   .attr('fill', rollingAvgColor)
  //   .attr('stroke', 'none');

  // annotation.append('text')
  //   .attr('dx', '10px')
  //   .attr('class', 'annotation-value')
  //   .append('tspan')
  //   .attr('font-weight', 600)
  //   .attr('fill', rollingAvgColor)
  //   .text(Math.round(lastData.avg));

  // annotation.append('text')
  //   .attr('dx', '10px')
  //   .attr('dy', '15px')
  //   .attr('class', 'annotation-value')
  //   .append('tspan')
  //   .attr('font-weight', 300)
  //   .attr('font-size', '12px')
  //   .attr('font-style', 'italic')
  //   .attr('fill', rollingAvgColor)
  //   .text('7-day avg');


  // Annotation for total value
  const annotationTotal = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[xScale(lastData.date), yScaleTotal(lastData.total)]})`);

  // annotationTotal.append('circle')
  //   .attr('r', '3')
  //   .attr('fill', barColor)
  //   .attr('stroke', 'none');

  annotationTotal.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value annotation-value-total')
    .append('tspan')
    .attr('font-weight', 600)
    .attr('fill', barColor)
    .text(Math.round(lastData.total));

  annotationTotal.append('text')
    .attr('dx', '10px')
    .attr('dy', '15px')
    .attr('class', 'annotation-value annotation-value-date')
    .text(d3.timeFormat('%b %-d')(lastData.date));

  window.pymChild.sendHeight();
}
